//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';

import HR from '@components/atoms/hr/HR';
import LabeledCheckbox from '@components/atoms/labeled-checkbox/LabeledCheckbox';
import { CheckboxSetSubFilter } from '@components/media-filters/CheckboxSetSubFilter';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import { DropdownFilter } from '@components/media-filters/DropdownFilter';
import {
  FilterOption,
  FilterSection,
  FilterSectionTitle,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import {
  DistributionFilterOption,
  getIsSelected,
  useDistributionOptions,
} from '@components/media-filters/useDistributionOptions';
import { useReelsQuery } from '@components/media-filters/useReelsQuery';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { isDefined, isUndefined } from '@utils/filterPredicates';

export const DEFAULT_DISTRIBUTION_FILTER_SECTION: FilterSection<boolean> = {
  paramKey: 'isDistributed',
  options: [
    { value: true, isSelected: false, label: 'Distributed to...' },
    { value: false, isSelected: false, label: 'Not distributed to...' },
  ],
};

export const DEFAULT_DISTRIBUTED_TO_FILTER_SECTION: FilterSection<string> = {
  paramKey: 'distribution',
  options: [],
};

export interface DistributionFilterProps {
  distributionSection?: FilterSection<boolean>;
  distributedToSection?: FilterSection<string>;
  initialIsCollapsed?: boolean;
  isDisabledDistribution?: boolean;
  isDisabledDistributedTo?: boolean;
  onChange: (section: FilterSection<FilterValue>) => void;
}

export const DistributionFilter = ({
  distributedToSection = DEFAULT_DISTRIBUTED_TO_FILTER_SECTION,
  distributionSection = DEFAULT_DISTRIBUTION_FILTER_SECTION,
  initialIsCollapsed,
  isDisabledDistributedTo,
  isDisabledDistribution,
  onChange,
}: DistributionFilterProps) => {
  const { data, isSuccess } = useReelsQuery({
    queryKey: [
      QueryKeyConstants.MediaFilters,
      QueryKeyConstants.MediaFiltersReels,
    ],
  });

  const selectedOption = distributionSection.options.find(
    (option) => option.isSelected,
  );

  const getOptions = useDistributionOptions();

  const options = useMemo(
    () => getOptions(distributedToSection.options, data ?? []),
    [distributedToSection, data],
  );

  const mapOptionsToSection = (
    options: DistributionFilterOption[],
    selected: FilterOption<string>[],
  ) => {
    return {
      ...distributedToSection,
      options: options
        .flatMap((option) => (option.subOptions ? option.subOptions : [option]))
        .map((option) => {
          const selectedOption = selected.find(
            (selectedOption) => selectedOption.label === option.label,
          );
          return {
            ...option,
            isSelected: selectedOption
              ? selectedOption.isSelected
              : getIsSelected(distributedToSection.options, option.value),
          };
        }),
    };
  };

  const onSelect = (selected: FilterOption<string>) => {
    onChange(mapOptionsToSection(options, [selected]));
  };

  const onSelectAll = (
    selected: FilterOption<string>[],
    isSelected: boolean,
  ) => {
    onChange(
      mapOptionsToSection(
        options,
        selected.map((option) => ({
          ...option,
          isSelected,
        })),
      ),
    );
  };

  const handleChangeIsDistributed = (section: FilterSection<boolean>) => {
    const selectedOption = section.options.find((option) => option.isSelected);

    if (isUndefined(selectedOption)) {
      onChange({
        ...distributedToSection,
        options: [],
      });
    }

    onChange(section);
  };

  useEffect(() => {
    // Update filter section after successfully retrieve reels.
    if (isSuccess) {
      onChange(mapOptionsToSection(options, []));
    }
  }, [data, isSuccess]);

  return (
    <>
      <CollapsibleSection
        isCollapsed={initialIsCollapsed}
        title={FilterSectionTitle[distributionSection.paramKey]}
      >
        <DropdownFilter
          isDisabled={isDisabledDistribution}
          onChange={handleChangeIsDistributed}
          placeholder='Not specified'
          section={distributionSection}
        />
        {isDefined(selectedOption) && (
          <CheckboxSet>
            {options
              .filter((option) => option.isEnabled)
              .map((option) => {
                if (
                  isDefined(option.subOptions) &&
                  option.subOptions.length > 0
                ) {
                  return (
                    <CheckboxSetSubFilter
                      key={option.label}
                      isDisabled={isDisabledDistributedTo}
                      label={option.label}
                      onChange={onSelect}
                      onChangeAll={(isSelected) =>
                        onSelectAll(option.subOptions ?? [], isSelected)
                      }
                      options={option.subOptions}
                    />
                  );
                } else {
                  return (
                    <LabeledCheckbox
                      key={option.label}
                      handleClick={(isSelected) =>
                        onSelect({ ...option, isSelected })
                      }
                      isChecked={option.isSelected}
                      label={option.label}
                      value={option.value}
                    />
                  );
                }
              })}
          </CheckboxSet>
        )}
      </CollapsibleSection>
      <HR />
    </>
  );
};

const CheckboxSet = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
    width: 100%;
`,
);
